import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, Clock, MapPin, Stethoscope, UserRound } from 'lucide-react';
import { getAppointmentPopups, dismissAppointmentPopup } from '../../api';
import { usePlayerData } from './PlayerDataContext';

// Shown over every player-app route (mounted in PlayerShell), not just Today — a push
// notification deep-links to /player/me/medical, and the popup has to be there too.
// The server decides what's due: a 'new' appointment until the player closes it once, and a
// 'today' reminder on the appointment's own day until closed again (see
// /api/players/me/appointment-popups). Re-checked whenever the app returns to the foreground,
// because an installed PWA usually resumes rather than reloading when it's reopened.

function fmtDay(d) {
  return new Date(d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}
function fmtTime(d) {
  return new Date(d).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

function relatedTo(a) {
  if (a.injury_body_part || a.injury_type) return [a.injury_body_part, a.injury_type].filter(Boolean).join(' · ');
  return a.illness_type || null;
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderTop: '1px solid var(--border)' }}>
      <Icon size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 2 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2, overflowWrap: 'anywhere' }}>{value}</div>
      </div>
    </div>
  );
}

export default function AppointmentPopup() {
  const navigate = useNavigate();
  const { sessionToken } = usePlayerData();
  const [queue, setQueue] = useState([]);
  // Closed this session — keeps a foreground re-check from resurfacing a popup whose dismiss
  // request is still in flight.
  const dismissedIds = useRef(new Set());

  const load = useCallback(() => {
    if (!sessionToken) return;
    getAppointmentPopups(sessionToken)
      .then(rows => setQueue((rows || []).filter(a => !dismissedIds.current.has(a.id))))
      .catch(() => {}); // a popup is never worth breaking the app over
  }, [sessionToken]);

  useEffect(() => {
    load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const appt = queue[0];
  if (!appt) return null;

  const close = (thenPath) => {
    dismissedIds.current.add(appt.id);
    setQueue(q => q.slice(1));
    dismissAppointmentPopup(sessionToken, appt.id).catch(() => {});
    if (thenPath) navigate(thenPath);
  };

  const isToday = appt.popup_kind === 'today';
  const accent = isToday ? '#f59e0b' : '#3b82f6';

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="appt-popup-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 24 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: 380, maxHeight: '100%', overflowY: 'auto', padding: 24, boxShadow: '0 40px 80px rgba(0,0,0,0.6)', color: 'var(--text-primary)' }}>
        <div style={{ textAlign: 'center' }}>
          {queue.length > 1 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>1 of {queue.length}</div>
          )}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: `${accent}1f`, color: accent, fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 99, marginBottom: 14 }}>
            <CalendarClock size={12} /> {isToday ? 'Today' : 'New appointment'}
          </div>
          <h2 id="appt-popup-title" style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>{appt.appointment_type}</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px' }}>
            {isToday ? `Don't forget — today at ${fmtTime(appt.appointment_date)}.` : 'The medical staff scheduled an appointment for you.'}
          </p>
        </div>

        <div style={{ marginBottom: 20 }}>
          <Row icon={CalendarClock} label="Date" value={fmtDay(appt.appointment_date)} />
          <Row icon={Clock}         label="Time" value={fmtTime(appt.appointment_date)} />
          <Row icon={MapPin}        label="Location" value={appt.location} />
          <Row icon={Stethoscope}   label="Related to" value={relatedTo(appt)} />
          <Row icon={UserRound}     label="Scheduled by" value={appt.scheduled_by} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={() => close()}
            style={{ padding: '12px 20px', borderRadius: 12, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', minHeight: 44 }}>
            {queue.length > 1 ? 'Got it — next' : 'Got it'}
          </button>
          <button onClick={() => close('/player/me/medical')}
            style={{ padding: '10px 20px', borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            View all my appointments
          </button>
        </div>
      </div>
    </div>
  );
}
